import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Calendar,
  MapPin,
  Users,
  Trophy,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { getEvent, listEventParticipants } from "../graphql/queries";
import { createEventParticipant } from "../graphql/mutations";
import { formatDate, isPast } from "../utils/dateUtils";
import { getUserByEmail } from "../lib/api/users";
import { ParticipantStatus } from "../API";
import {
  COMMANDER_CATEGORIES,
  CommanderCategory,
} from "../lib/commander";

const client = generateClient();

const EventRegistration: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isRegistering, setIsRegistering] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [commanderCategory, setCommanderCategory] =
    useState<CommanderCategory>("CASUAL");

  // Get current user from stored token
  React.useEffect(() => {
    const getCurrentUser = async () => {
      const storedToken = sessionStorage.getItem("id_token");
      if (!storedToken) return;

      try {
        const payload = JSON.parse(atob(storedToken.split(".")[1]));
        const userEmail = payload.email;

        if (userEmail) {
          const user = await getUserByEmail(userEmail);
          setCurrentUser(user);
        }
      } catch (error) {
        console.error("Error getting current user:", error);
      }
    };

    getCurrentUser();
  }, []);

  // Fetch event details
  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  // Fetch current participants
  const {
    data: participants,
    isLoading: participantsLoading,
    error: participantsError,
  } = useQuery({
    queryKey: ["eventParticipants", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventParticipants,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 100,
        },
      });
      return result.data.listEventParticipants.items;
    },
    enabled: !!eventId,
  });

  // Register for event
  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!currentUser) throw new Error("User not found");

      const result = await client.graphql({
        query: createEventParticipant,
        variables: {
          input: {
            eventId: eventId!,
            userId: currentUser.id,
            registrationDate: new Date().toISOString(),
            status: ParticipantStatus.REGISTERED,
            commanderCategory:
              event?.format === "Commander"
                ? (commanderCategory as any)
                : null,
          },
        },
      });
      return result.data.createEventParticipant;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["eventParticipants", eventId],
      });
      setIsRegistering(false);
      alert("Successfully registered for event!");
    },
    onError: (error: any) => {
      console.error("Error registering for event:", error);
      setIsRegistering(false);
      alert("Failed to register for event. Please try again.");
    },
  });

  const handleRegister = () => {
    if (!currentUser) {
      alert("Please log in to register for events");
      return;
    }
    setIsRegistering(true);
    registerMutation.mutate();
  };

  // Check if user is already registered
  const currentParticipant = participants?.find(
    (participant: any) => participant.userId === currentUser?.id
  );
  const isUserRegistered = !!currentParticipant;

  // Check if event is full (assuming max 32 participants for now)
  const isEventFull = participants && participants.length >= 32;

  // Check if event is in the past
  const isEventPast = event && isPast(event.date);

  // Check if event is completed
  const isEventCompleted = event?.isCompleted;

  if (eventLoading || participantsLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (eventError || participantsError || !event) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading event details
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/event/${eventId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Event
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">Event Registration</h1>
          <p className="text-muted-foreground mt-2">
            Register for {event.name}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Event Details */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Event Information</CardTitle>
              <CardDescription>Details about this event</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(event.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4" />
                <span>{event.location}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Trophy className="h-4 w-4" />
                <span>{event.format}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4" />
                <span>{participants?.length || 0} / 32 participants</span>
              </div>
              {event.description && (
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Description</h4>
                  <p className="text-sm text-muted-foreground">
                    {event.description}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Registration Status */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Registration Status</CardTitle>
              <CardDescription>Your registration information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!currentUser ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Login Required</h3>
                  <p className="text-muted-foreground mb-4">
                    Please log in to register for this event.
                  </p>
                  <Button onClick={() => navigate("/")}>Go to Login</Button>
                </div>
              ) : isUserRegistered ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    {currentParticipant.status === ParticipantStatus.DROPPED
                      ? "Dropped from Event"
                      : "Already Registered"}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {currentParticipant.status === ParticipantStatus.DROPPED
                      ? "An admin can restore you before the next round."
                      : "You are registered for this event."}
                  </p>
                  {event.format === "Commander" && (
                    <p className="mb-4 text-sm text-muted-foreground">
                      Category:{" "}
                      {COMMANDER_CATEGORIES.find(
                        ({ value }) =>
                          value ===
                          participants?.find(
                            (participant: any) =>
                              participant.userId === currentUser.id
                          )?.commanderCategory
                      )?.label || "Not selected"}
                    </p>
                  )}
                  <Badge
                    variant={
                      currentParticipant.status === ParticipantStatus.DROPPED
                        ? "destructive"
                        : "default"
                    }
                    className="text-lg px-4 py-2"
                  >
                    {currentParticipant.status === ParticipantStatus.DROPPED
                      ? "Dropped"
                      : "Registered"}
                  </Badge>
                </div>
              ) : isEventPast ? (
                <div className="text-center py-8">
                  <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    Event Has Passed
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    This event has already occurred.
                  </p>
                  <Badge variant="destructive" className="text-lg px-4 py-2">
                    Event Ended
                  </Badge>
                </div>
              ) : isEventCompleted ? (
                <div className="text-center py-8">
                  <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    Event Completed
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    This event has been completed.
                  </p>
                  <Badge variant="destructive" className="text-lg px-4 py-2">
                    Completed
                  </Badge>
                </div>
              ) : isEventFull ? (
                <div className="text-center py-8">
                  <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Event Full</h3>
                  <p className="text-muted-foreground mb-4">
                    This event has reached maximum capacity.
                  </p>
                  <Badge variant="destructive" className="text-lg px-4 py-2">
                    Full
                  </Badge>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-2">
                      Ready to Register
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Click the button below to register for this event.
                    </p>
                  </div>
                  {event.format === "Commander" && (
                    <div className="mb-4 space-y-2 text-left">
                      <label className="text-sm font-medium">
                        Commander category
                      </label>
                      <Select
                        value={commanderCategory}
                        onValueChange={(value) =>
                          setCommanderCategory(value as CommanderCategory)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COMMANDER_CATEGORIES.map(({ value, label }) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        An admin can adjust this before pods are generated.
                      </p>
                    </div>
                  )}
                  <Button
                    onClick={handleRegister}
                    disabled={isRegistering}
                    size="lg"
                    className="w-full"
                  >
                    {isRegistering ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Registering...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Register for Event
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    You will be registered as: {currentUser.displayName}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EventRegistration;
